import { githubUrl, jiraBrowseUrl } from "../../../utils/urls";

// Markers the backend bakes into commit_message. Both are prefixes and can appear in
// either order, so stripping loops until the message stops changing.
const DOUBLE_COMMIT_PREFIX = /^\s*double commit\s*:\s*/i;
const NODC_PREFIX = /^\s*::\s*nodc\s*::\s*/i;
// A PR number is not a field; only a plain trailing (#1234) is safe to lift out.
const PR_SUFFIX = /\s*\(#(\d+)\)\s*$/;

/**
 * Message split into the text to render, the NODC marker and the PR number.
 * An unparseable tail leaves `pr` empty and the message untouched.
 * @param {string} raw
 * @returns {{text: string, nodc: boolean, pr: string}} */
export const parseCommitMessage = (raw = "") => {
  let text = raw || "";
  let nodc = false;

  for (let stripped = true; stripped; ) {
    stripped = false;
    if (DOUBLE_COMMIT_PREFIX.test(text)) {
      text = text.replace(DOUBLE_COMMIT_PREFIX, "");
      stripped = true;
    }
    if (NODC_PREFIX.test(text)) {
      text = text.replace(NODC_PREFIX, "");
      nodc = true;
      stripped = true;
    }
  }

  const match = text.match(PR_SUFFIX);
  if (match) text = text.slice(0, match.index);

  return { text: text.trim(), nodc, pr: match ? match[1] : "" };
};

// Tags are heterogeneous (0.148.4, 0.0.231, v0.147.3) and are never normalised, so they
// are ordered as numeric-aware strings rather than parsed into versions.
const byTagDesc = (a, b) =>
  String(b || "").localeCompare(String(a || ""), undefined, { numeric: true });

const flattenCommits = (repositories = []) => {
  const rows = [];
  (repositories || []).forEach((repository) => {
    const repoName = repository?.repo_name || "";
    (repository?.commits || []).forEach((commit, index) => {
      const { text, nodc, pr } = parseCommitMessage(commit?.commit_message);
      // A commit is a double commit iff the backend says so. The "Double Commit:"
      // message prefix is only stripped for display, never used to classify.
      const flagKind = commit?.is_double_commit
        ? "double"
        : nodc
        ? "nodc"
        : "none";
      rows.push({
        id: `${repoName}:${commit?.commit_hash || ""}:${index}`,
        repoName,
        tag: commit?.commit_tag || "",
        jiraId: commit?.jira_id || "",
        hash: commit?.commit_hash || "",
        message: text,
        nodc,
        pr,
        flagKind,
        isDouble: flagKind === "double",
      });
    });
  });
  return rows.sort(
    (a, b) => a.repoName.localeCompare(b.repoName) || byTagDesc(a.tag, b.tag)
  );
};

/**
 * Releases with every patch flattened to a single commit list. `patch_label` repeats
 * `release_label` on the base patch and is kept as-is.
 * @param {any[]} response
 * @returns {any[]} */
export const adaptReleaseView = (response = []) =>
  (response || []).map((release) => {
    const label = release?.release_label || "";
    return {
      label,
      patches: (release?.patches || []).map((patch, index) => ({
        key: `${label}/${index}/${patch?.patch_label || ""}`,
        label: patch?.patch_label || "",
        releaseLabel: label,
        commits: flattenCommits(patch?.repositories),
      })),
    };
  });

/** @param {any[]} commits @returns {number} */
export const repoCount = (commits = []) =>
  new Set(commits.map((commit) => commit.repoName)).size;

/**
 * Lowest to highest tag carried by a patch, or the single tag when they all match.
 * @param {any[]} commits
 * @returns {string} */
export const tagRange = (commits = []) => {
  const tags = commits
    .map((commit) => commit.tag)
    .filter(Boolean)
    .sort(byTagDesc);
  if (!tags.length) return "";
  const [highest] = tags;
  const lowest = tags[tags.length - 1];
  return highest === lowest ? highest : `${lowest} \u2013 ${highest}`;
};

/** @param {any} commit @param {string} query already lower-cased and trimmed @returns {boolean} */
export const matchesQuery = (commit, query) =>
  !query ||
  [commit.message, commit.jiraId, commit.repoName, commit.hash].some((field) =>
    (field || "").toLowerCase().includes(query)
  );

/** @param {any} commit @param {string} segment @returns {boolean} */
export const matchesSegment = (commit, segment) => {
  if (segment === "double") return commit.isDouble;
  if (segment === "original") return !commit.isDouble;
  if (segment === "noTicket") return !commit.jiraId;
  return true;
};

/**
 * Segment totals across every patch in scope, not just the first one.
 * @param {any[]} commits
 * @returns {{all: number, double: number, original: number, noTicket: number}} */
export const segmentCounts = (commits = []) => ({
  all: commits.length,
  double: commits.filter((commit) => commit.isDouble).length,
  original: commits.filter((commit) => !commit.isDouble).length,
  noTicket: commits.filter((commit) => !commit.jiraId).length,
});

/**
 * Why the selection yields nothing, phrased for the empty banner.
 * @param {{query: string, segment: string, repositories: string[], hasCommits: boolean}} state
 * @returns {string} */
export const emptyReason = ({ query, segment, repositories, hasCommits }) => {
  if (!hasCommits) return "these patches carry no commits at all";
  if (query) return `nothing matches "${query}"`;
  if (segment === "double") return "none of their commits are double commits";
  if (segment === "original") return "every commit here is a double commit";
  if (segment === "noTicket") return "every commit already has a ticket";
  if (repositories?.length)
    return "none of their commits touch the selected repositories";
  return "the current filters exclude every commit";
};

/** @param {string} jiraId @returns {string} */
export const jiraTicketUrl = (jiraId) => `${jiraBrowseUrl}/${jiraId}`;

/** @param {string} repoName @param {string} hash @returns {string} */
export const commitUrl = (repoName, hash) =>
  `${githubUrl}/${repoName}/commit/${hash}`;

/** @param {string} repoName @param {string} pr @returns {string} */
export const pullRequestUrl = (repoName, pr) =>
  `${githubUrl}/${repoName}/pull/${pr}`;

/** @param {number} count @param {string} noun @returns {string} */
export const plural = (count, noun) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;
